/**
 * TAN-9802 — Foco al landmark principal en cada cambio de ruta (WCAG 2.4.3 Focus Order).
 *
 * En una SPA la navegación no recarga el documento, así que el foco se queda donde estaba: en el
 * enlace del menú que ya no existe, o —cuando React desmonta ese elemento— en `<body>`, sin
 * posición. Quien navega con teclado o lector de pantalla acaba en la pantalla nueva pero con el
 * cursor en la anterior, y el siguiente Tab lo lleva a un sitio arbitrario. Este hook devuelve el
 * foco al landmark principal (`main`, `id="main-content"`) de la pantalla que acaba de aterrizar.
 *
 * **La señal es `pathname`, no `routeTitle`.** Dos rutas distintas pueden compartir `titleKey`
 * (`/course/:guid` y su pestaña de panel, por ejemplo), y un título repetido no dispararía el
 * efecto aunque la pantalla haya cambiado entera. El `pathname` sí distingue esos casos, y de
 * paso deja fuera los cambios de solo query (`?tab=2`, un filtro, una paginación): ahí el usuario
 * sigue en la misma pantalla y moverle el foco sería robárselo del control que acaba de usar.
 *
 * **Primero el blur, y es lo que cubre las rutas sin `<main>`.** No todas las pantallas montan un
 * layout de página (`isMainLandmark`), así que en unas cuantas no hay destino al que ir. Soltar el
 * foco obsoleto en el acto deja el documento en un estado sano y conocido —`<body>`, sin posición—
 * desde el que el siguiente Tab empieza por el principio del documento: por el skip link. Es peor
 * que enfocar el `<main>`, pero es determinista, y no depende de que la pantalla exista.
 *
 * **El intento de foco es diferido, y por eso hay observer.** Las páginas se cargan con
 * `React.lazy`: en el momento en que este efecto corre, lo que hay pintado es el fallback del
 * `<Suspense>` y el `<main>` de la ruta nueva todavía no existe (en Edelvives el `<Suspense>` está
 * en `App.js`, por encima de este handler, así que ni siquiera podemos apoyarnos en su orden de
 * montaje). Se intenta en el siguiente frame y, si aún no está, se espera con un `MutationObserver`
 * sobre `document.body` que reintenta en cada tanda de mutaciones. Un `setTimeout` de seguridad
 * corta la espera: si el chunk no baja, se desiste en silencio y el blur ya dejó el estado sano.
 *
 * También se comprueba que el foco **haya prendido**: un `<main>` oculto (Suspense legacy con
 * `display: none`) no lo acepta, y `focus()` no lanza — simplemente no hace nada. Sin esa
 * comprobación daríamos por bueno un foco que sigue en `<body>` y dejaríamos de esperar.
 *
 * **Mientras se espera, el foco del usuario manda.** Entre el blur y la llegada del chunk pueden
 * pasar segundos, y hay UI enfocable fuera del `<Suspense>` (el chat, un toast) que el usuario
 * puede haber alcanzado ya con el teclado. Si al llegar el `<main>` el foco está reclamado, se
 * desiste: quitárselo entonces sería justo el robo que este hook existe para evitar.
 *
 * **El cleanup es lo que resuelve los redirects encadenados.** Varias rutas aterrizan con uno o
 * más `<Redirect>` (`history.replace`), y cada salto es un cambio de `pathname`. Como el foco no
 * se mueve nunca de forma síncrona, el intento del salto intermedio se cancela —`cancelAnimationFrame`,
 * `observer.disconnect()`, `clearTimeout`— antes de llegar a enfocar nada: solo enfoca el último
 * destino, el real.
 *
 * **En el primer render no hace nada.** La carga inicial ya deja el foco al principio del
 * documento; moverlo al `<main>` ahí saltaría por encima del skip link y del header, que es
 * exactamente lo que un usuario de teclado espera poder tabular nada más entrar.
 *
 * `preventScroll: true` por el mismo motivo que en `ui/atoms/SkipLink/useSkipLink.ts`: Chrome
 * alinea el elemento enfocado con «center if needed» y, como el `<main>` es más alto que el
 * viewport, centrarlo dejaría al usuario a media página. Sin scroll, la vista se queda arriba.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { MAIN_CONTENT_ID } from 'core/layout/constants';
import { logToSentry } from 'core/utils/sentry';

/** Cuánto se espera al `<main>` de la ruta nueva antes de desistir (el chunk puede no llegar nunca). */
export const FOCUS_TIMEOUT_MS = 5000;

const FOCUS_FAILURE_MESSAGE = 'No se pudo mover el foco al contenido principal';

/**
 * Enfoca el landmark principal si ya está en el DOM. Devuelve si el foco prendió de verdad: el
 * elemento puede existir y no aceptarlo (oculto), y `focus()` no avisa de ello.
 */
function tryFocusMain(): boolean {
  const main = document.getElementById(MAIN_CONTENT_ID);
  if (!main) return false;

  main.focus({ preventScroll: true });

  return document.activeElement === main;
}

/**
 * Si nadie ha reclamado el foco todavía: sigue donde lo dejó el blur —en el documento, sin
 * posición— y moverlo al `<main>` no se lo quita a nadie.
 */
function isFocusUnclaimed(): boolean {
  const { activeElement } = document;

  return activeElement === null || activeElement === document.body || activeElement === document.documentElement;
}

/**
 * Mover el foco es una mejora de accesibilidad, nunca un motivo para tirar la pantalla: este hook
 * vive por encima del `<Switch>` y `ChunkLoadErrorBoundary` relanza cualquier error que no sea de
 * carga de chunk, así que una excepción aquí dejaría la aplicación entera en blanco. Se degrada a
 * «el foco se queda donde esté» y se reporta.
 *
 * El mensaje y la ruta van también en `extra`: `logToSentry` captura la excepción cuando hay un
 * `Error`, y por ese camino Sentry se queda con el stacktrace y descarta el mensaje y el nivel.
 */
function reportFocusFailure(error: unknown): void {
  logToSentry('warning', FOCUS_FAILURE_MESSAGE, {
    error: error instanceof Error ? error : new Error(String(error)),
    tags: { feature: 'route-focus' },
    extra: { message: FOCUS_FAILURE_MESSAGE, pathname: window.location.pathname },
  });
}

function useRouteFocusHandler(): void {
  const { pathname } = useLocation();
  const isFirstRenderRef = useRef<boolean>(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;

      return undefined;
    }

    let animationFrameId: number | null = null;
    let observer: MutationObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    function stopWaitingForMain(): void {
      observer?.disconnect();
      observer = null;

      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }

    /** Intenta enfocar. Devuelve si ya no queda nada que hacer: o prendió, o falló sin remedio. */
    function settleFocus(): boolean {
      try {
        if (!tryFocusMain()) return false;
      } catch (error) {
        stopWaitingForMain();
        reportFocusFailure(error);

        return true;
      }

      stopWaitingForMain();

      return true;
    }

    function waitForMain(): void {
      observer = new MutationObserver(() => {
        // Si el usuario ya se ha llevado el foco a otra cosa mientras bajaba el chunk, se desiste:
        // el `<main>` que aterriza tarde no tiene derecho a quitárselo.
        if (!isFocusUnclaimed()) {
          stopWaitingForMain();

          return;
        }

        settleFocus();
      });

      observer.observe(document.body, { childList: true, subtree: true });
      timeoutId = setTimeout(stopWaitingForMain, FOCUS_TIMEOUT_MS);
    }

    try {
      // El blur va aquí, síncrono: el elemento enfocado pertenece a la pantalla que se está
      // desmontando y no debe sobrevivir ni un frame a la navegación.
      const { activeElement } = document;
      if (activeElement instanceof HTMLElement && activeElement !== document.body) {
        activeElement.blur();
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;

        // Aquí no hace falta comprobar quién tiene el foco: el blur acaba de dejarlo en el
        // documento, un frame antes, y nadie ha podido reclamarlo en ese hueco.
        if (!settleFocus()) waitForMain();
      });
    } catch (error) {
      reportFocusFailure(error);
    }

    return () => {
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId);

      stopWaitingForMain();
    };
  }, [pathname]);
}

export default useRouteFocusHandler;
